import { useNavigate } from 'react-router-dom';
import { BreadcrumbGroup } from '@cloudscape-design/components';
import { Asset } from '../../types';
import { Layout } from '../../components/layout';
import { useAuth } from 'react-oidc-context';
import { getEndpoint } from '../../utils';
import AssetForm from '../../components/admin/AssetForm';
import { useState, useCallback } from 'react';

export default function AddAsset() {
    const navigate = useNavigate();
    const auth = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = useCallback(async (data: FormData | { asset: Asset, fileContent?: string, contentType?: string }) => {
        try {
            setIsSubmitting(true);
            auth.startSilentRenew();
            
            // Extract file and asset data
            let file: File | undefined;
            let assetData: Asset;
            
            if (data instanceof FormData) {
                const fileEntry = data.get('file') as File;
                const assetEntry = data.get('asset');
                if (!assetEntry || !fileEntry) {
                    throw new Error('Missing required data');
                }
                file = fileEntry;
                assetData = JSON.parse(assetEntry as string);
            } else {
                throw new Error('FormData is required');
            }

            // First, create the asset and get the upload URL
            const createResponse = await fetch(getEndpoint('/api/admin/assets'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${auth?.user?.id_token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...assetData,
                    fileType: file.type
                })
            });

            if (!createResponse.ok) {
                throw new Error('Failed to create asset');
            }

            const { uploadUrl } = await createResponse.json();

            // Then, upload the file directly to S3
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type
                }
            });

            if (!uploadResponse.ok) {
                throw new Error('Failed to upload file');
            }

            navigate('/admin');
        } catch (error) {
            console.error('Failed to save asset:', error);
            setIsSubmitting(false);
        }
    }, [auth, navigate]);

    return (
        <Layout breadcrumbs={
            <BreadcrumbGroup
                onClick={({ detail }) => navigate(detail.href)}
                items={[
                    { text: "Admin", href: "/admin" },
                    { text: "Add Asset", href: "/admin/add-asset" }
                ]}
            />
        }>
            <AssetForm onSubmit={handleSubmit} isSubmitting={isSubmitting} setIsSubmitting={setIsSubmitting} />
        </Layout>
    );
}
